/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import Button, { Icons } from "../buttons/Button";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import {
  errorAlert,
  getUniqueCombination,
  makeApiRequest,
  successAlert,
} from "../../util";

import AuthContext from "../../store/authContext";
import InlineLoader from "../loader/InlineLoader";
import EditDFSIConnectionRowFormFields from "./EditDFSIConnectionRowFormFields";
import {
  EDIT_DFSI_CONNECTION_FORM_FIELDS,
  fieldNameMapping,
} from "../../constants";
import {
  EDIT_DFSI_CONNECTION_TRANSLATIONS,
  EDIT_ROW_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
} from "../../translations/util";

interface IProps {
  setShowEditPanel: any;
  org: string;
  panelName: string;
  esRef: string;
  initialState: any;
  formFields: any;
  header: string;
  transformResponseRow: any;
  transformUpdateBody?: any;
  onUpdate?: any;
  loadDataByRef: any;
  deleteModalItemName?: any;
  onDelete?: any;
  hasDeletePermission?: boolean;
  hasEditPermission?: boolean;
  refreshTable?: any;
}

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = EDIT_DFSI_CONNECTION_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.({
              version: data["DFSI-VERSION"],
              val: data[field.name],
              role: data["IWF-ROLE"],
            });
          case "DROPDOWN":
          case "NUMBER":
            return field.isInputInvalid?.({
              version: data["DFSI-VERSION"],
              val: data[field.name],
              role: data["IWF-ROLE"],
            });
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const EditDFSIConnectionRow = ({
  setShowEditPanel,
  onDelete,
  esRef,
  org,
  panelName,
  initialState,
  formFields,
  header,
  transformResponseRow,
  transformUpdateBody,
  onUpdate,
  loadDataByRef,
  deleteModalItemName,
  hasDeletePermission = false,
  hasEditPermission = false,
  refreshTable,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const { token } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [data, setData] = useState(initialState);
  const [initialData, setInitialData] = useState(data);
  const [loading, setLoading] = useState(false);
  const [allDFSIconnections, setAllDFSIconnections] = useState<any[]>([]);

  const validateUniqueCombos = ({
    allRecords,
    currentRecord,
    fieldCombos,
  }: {
    allRecords: Array<Record<string, any>>;
    currentRecord: Record<string, any>;
    fieldCombos?: string[][];
  }): boolean => {
    const failedCombo = fieldCombos?.find((combo) => {
      return allRecords.some((record) => {
        return combo.every((field) => {
          return (
            record["CONNECTION-ID"] !== currentRecord["CONNECTION-ID"] &&
            record[field] === currentRecord[field]
          );
        });
      });
    });

    if (failedCombo) {
      errorAlert(
        `The combination of ${fieldNameMapping[failedCombo[0]]} and ${
          fieldNameMapping[failedCombo[1]]
        } is not unique`
      );
      return false;
    }
    return true;
  };

  const getDataByRef = async (ref: string) => {
    if (org && token) {
      setDataLoading(true);
      try {
        const response = await loadDataByRef({
          token,
          org,
          ref,
        });
        setData(transformResponseRow(response));
        setInitialData(transformResponseRow(response));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const updateData = async () => {
    setLoading(true);
    try {
      await onUpdate({
        body: transformUpdateBody(data),
        token,
        ref: esRef,
        org,
      });
      if (refreshTable) {
        refreshTable();
      }
      getDataByRef(esRef);
      successAlert(EDIT_DFSI_CONNECTION_TRANSLATIONS.successAlertMessage);
      setIsEditing(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDFSIConnections = async () => {
    setLoading(true);
    try {
      if (!org) return;

      let page = 1;
      const pageSize = 100;
      let allRows: any[] = [];
      let totalPages = 1;

      do {
        const queryParams = new URLSearchParams({
          page: `${page}`,
          pageSize: `${pageSize}`,
        });

        // eslint-disable-next-line no-await-in-loop
        const response = await makeApiRequest<{
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
          Page: number;
        }>({
          path: `/api/v1/app-config/dfsi-connections?${queryParams}`,
          token,
          org,
        });
        allRows = [...allRows, ...response.Rows];
        totalPages = response.NbrOfPages;
        // eslint-disable-next-line no-plusplus
        page++;
      } while (page <= totalPages);

      setAllDFSIconnections(allRows); // Final set after all data is fetched
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDFSIConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getDataByRef(esRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esRef]);

  return (
    <>
      <aside
        id="sidepanel"
        data-cy="editDFSIConnectionRow-sidepanel"
        className="sidepanel sidepanel-edit fixed z-40 h-full transform transition-transform ease-in duration-150 bg-white"
      >
        <div className="panel-header">
          <div className="panel-left-side">
            <Button
              kind="ghost"
              className="close-btn"
              icon={Icons.Close}
              onClick={() => setShowEditPanel()}
              dataCy="closeEditDFSIConnectionRow-"
            />
            <h5 data-cy="editDFSIConnectionRow-panelName"> {panelName} </h5>
          </div>

          {!isEditing ? (
            <div className="panel-right-side tab-header-buttons">
              {onUpdate ? (
                <Button
                  className="w-sm mr-2"
                  kind="tertiary"
                  icon={Icons.Edit}
                  disabled={!hasEditPermission}
                  label={EDIT_DFSI_CONNECTION_TRANSLATIONS.btnLabel.edit}
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  dataCy="dfsiConnectionSidepanelEdit-"
                />
              ) : null}

              {onDelete ? (
                <Button
                  kind="tertiary"
                  className="w-sm"
                  label={EDIT_DFSI_CONNECTION_TRANSLATIONS.btnLabel.delete}
                  disabled={!hasDeletePermission}
                  icon={Icons.Delete}
                  onClick={() => {
                    return setOpen(true);
                  }}
                  dataCy="dfsiConnectionSidepanelDelete-"
                />
              ) : null}
            </div>
          ) : (
            <div className="tab-header-buttons">
              <Button
                className="w-sm mr-2"
                type="button"
                kind="tertiary"
                disabled={false}
                label={EDIT_DFSI_CONNECTION_TRANSLATIONS.btnLabel.cancel}
                onClick={() => {
                  setOpenEdit(true);
                }}
                dataCy="dfsiConnectionSidepanelCancel-"
              />
              <Button
                className="w-sm"
                type="button"
                loading={loading}
                disabled={isFormInvalid({ data })}
                onClick={() => {
                  const result = validateUniqueCombos({
                    allRecords: allDFSIconnections,
                    currentRecord: data,
                    fieldCombos:
                      data["DFSI-VERSION"] && data["IWF-ROLE"]
                        ? getUniqueCombination(
                            data["DFSI-VERSION"],
                            data["IWF-ROLE"]
                          )
                        : [[]],
                  });
                  if (result) {
                    updateData();
                  }
                }}
                label={EDIT_DFSI_CONNECTION_TRANSLATIONS.btnLabel.save}
                dataCy="dfsiConnectionSidepanelSave-"
              />
            </div>
          )}
        </div>

        <div className="panel-content">
          <div className="data-tabs">
            {dataLoading ? (
              <InlineLoader
                description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
              />
            ) : (
              <EditDFSIConnectionRowFormFields
                data={data}
                isEditing={isEditing}
                esRef={esRef}
                setData={setData}
                formFields={formFields}
              />
            )}
          </div>
        </div>
      </aside>
      <DeleteModal
        modalHeaderText={EDIT_DFSI_CONNECTION_TRANSLATIONS.cancelModal.header(
          header
        )}
        modalDescriptionText={
          EDIT_DFSI_CONNECTION_TRANSLATIONS.cancelModal.description
        }
        onDelete={() => {
          setIsEditing(false);
          setData(initialData);
          setOpenEdit(false);
        }}
        open={openEdit}
        setOpen={setOpenEdit}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={EDIT_DFSI_CONNECTION_TRANSLATIONS.deleteModal.header(
          header
        )}
        modalDescriptionText={EDIT_DFSI_CONNECTION_TRANSLATIONS.deleteModal.description(
          header,
          data?.[deleteModalItemName]
        )}
        onDelete={() => onDelete(esRef)}
        open={open}
        setOpen={setOpen}
      />
    </>
  );
};

export default EditDFSIConnectionRow;
